
import aliasb03645cf6abd4d56a73b13687765a5d8 from '@/locale/i18nUtils.js';
import proxyFile from "../../proxy";
import api from '@/api/';
import {getToken} from "@/utils/cookie";

/**
 * ============================================================
 * WebSocket 消息类型对照表
 * ============================================================
 * | 消息类型 | 使用组件 | 业务说明 |
 * |---------|---------|----------|
 * | notice | header.vue, newsBox.vue | 系统消息通知更新，刷新未读数 |
 * | pending | left.vue, distributor/left.vue, platformOrder.vue | 待办数量变化 |
 * | sync | collectProducts.vue, alibabaProductModal.vue, productSalesPriceConfig.vue, BillUpdateCompletedModal.vue | 1688商品同步/销售价规则同步/账单更新 |
 * | auth | platformStoreList.vue, platformAuthorization.vue | 第三方平台授权成功 |
 * | save | platformStoreList.vue, platformAuthorization.vue | 第三方平台添加账号成功 |
 * | pay | purchaseStoreLimitModal.vue, rechargeRecord.vue | 店铺额度/充值支付结果 |
 * | sale | generateSalesRecords.vue | 售卖资料生成结果 |
 * ============================================================
 */

const webSocketList = [
  {pathname: '/yms.html', webSocketApi: api.get_ymsWebsocket},
  {pathname: '/supplier.html', webSocketApi: api.get_supplierWebsocket},
  {pathname: '/distributor.html', webSocketApi: api.get_distributorWebsocket}
];

/**
 * 根据当前访问路径获取对应系统的 websocket API。
 * @param {string} pathname 当前页面路径。
 * @returns {string} 当前系统使用的 websocket API。
 */
const getWebSocketApi = (pathname) => {
  if (!pathname.includes('/yms.html') && !pathname.includes('/supplier.html') && !pathname.includes('/distributor.html')) {
    return webSocketList[2].webSocketApi;
  }
  const match = webSocketList.find((item) => pathname.includes(item.pathname));
  return match?.webSocketApi || '';
};

/**
 * 根据当前域名生成 websocket 连接目标域。
 * @param {string} host 当前页面 host。
 * @param {string} origin 当前页面 origin。
 * @returns {string} websocket 连接域名。
 */
const getWebSocketTarget = (host, origin) => {
  if (host.includes('localhost')) {
    let domainName = proxyFile['/'].target.split('//')[1];
    return domainName.replace(/\/$/, '');
  }
  if (origin.includes('shop') && origin.includes('.yms.com')) {
    return origin.includes('test.pvt.yms.com') ? 'newerp-test.tongtool.com' : 'www.yms.com';
  }
  return host;
};

export default {
  socket: null,
  socketUrl: '',
  heartTimeOut: 25 * 1000,
  heartbeatTimer: null,
  messageListeners: new Map(),

  /**
   * 清理当前心跳计时器，避免重复发送保活消息。
   * @returns {object} 当前 websocket 管理器实例。
   */
  heartReset() {
    clearTimeout(this.heartbeatTimer);
    this.heartbeatTimer = null;
    return this;
  },

  /**
   * 在连接存活时重新启动心跳计时器。
   * @returns {object} 当前 websocket 管理器实例。
   */
  heartStart() {
    this.heartReset();
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return this;
    }
    this.heartbeatTimer = setTimeout(() => {
      try {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send("keep online test");
        }
      } catch (e) {
        console.error('websocket heartbeat error:', e);
      }
    }, this.heartTimeOut);
    return this;
  },

  /**
   * 解析 websocket 消息体，统一把 JSON 字符串转成对象。
   * @param {MessageEvent} event websocket 原始事件对象。
   * @returns {Object|null} 解析后的消息对象，解析失败返回 null。
   */
  parseMessage(event) {
    if (!event?.data) return null;
    try {
      return JSON.parse(event.data);
    } catch (e) {
      return null;
    }
  },

  /**
   * 注册 websocket 消息监听。
   * @param {string} key 监听器唯一标识，一般使用组件名 + _uid。
   * @param {Function} handler 收到消息后的回调函数。
   * @param {object} vm 组件实例，用于检测组件是否已销毁。
   * @returns {Function} 对应监听器的取消订阅函数。
   */
  subscribeMessage(key, handler, vm) {
    if (!key || typeof handler !== 'function') {
      return () => {};
    }
    this.messageListeners.set(key, { handler, vm });
    return () => this.unsubscribeMessage(key);
  },

  /**
   * 移除指定 websocket 消息监听。
   * @param {string} key 监听器唯一标识。
   */
  unsubscribeMessage(key) {
    if (!key) return;
    this.messageListeners.delete(key);
  },

  /**
   * 把原始 websocket 消息广播给所有订阅者。
   * 自动清理已销毁组件的监听器，避免内存泄漏。
   * @param {MessageEvent} event websocket 原始事件对象。
   */
  notifyMessageListeners(event) {
    const toDelete = [];
    this.messageListeners.forEach(({ handler, vm }, key) => {
      try {
        if (vm && (vm._isDestroyed || vm._isDestroyed === true)) {
          toDelete.push(key);
          return;
        }
        handler(event);
      } catch (e) {
        console.error(`websocket listener error: ${key}`, e);
      }
    });
    toDelete.forEach(key => this.messageListeners.delete(key));
  },

  /**
   * 生成当前页面使用的 websocket 连接地址。
   * @returns {string} websocket 完整连接地址。
   */
  getSocketUrl() {
    const token = getToken();
    if (!token) return '';
    const pathname = window.location.pathname;
    const origin = window.location.origin;
    const host = window.location.host;
    const target = getWebSocketTarget(host, origin);
    const webSocketApi = getWebSocketApi(pathname);
    const protocol = window.location.href.includes('https') ? 'wss://' : 'ws://';
    return webSocketApi ? `${protocol}${target}${webSocketApi}${token}` : '';
  },

  /**
   * 绑定当前连接的 open、message、error、close 事件。
   */
  bindSocketEvents() {
    if (!this.socket) return;
    this.socket.onopen = (e) => {
      this.heartReset().heartStart();
      console.log('------WebSocket连接成功------', e);
    };
    this.socket.onerror = (e) => {
      console.log('----WebSocket连接发生错误----', e);
    };
    this.socket.onmessage = (e) => {
      this.heartReset().heartStart();
      this.notifyMessageListeners(e);
    };
    this.socket.onclose = (e) => {
      this.heartReset();
      console.log('---WebSocket 断开连接----', e);
    };
  },

  /**
   * 初始化 websocket 单例连接。
   * @returns {WebSocket|null} 当前可用的 websocket 连接实例。
   */
  initWebSocket() {
    try {
      if (!("WebSocket" in window)) {
        console.log('您的浏览器不支持websocket');
        return null;
      }
      const socketUrl = this.getSocketUrl();
      if (!socketUrl) {
        return null;
      }
      if (this.socket && this.socketUrl === socketUrl) {
        const readyState = this.socket.readyState;
        if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
          return this.socket;
        }
      }
      this.destroyWebSocket(false);
      this.socketUrl = socketUrl;
      this.socket = new WebSocket(socketUrl);
      this.bindSocketEvents();
      return this.socket;
    } catch (e) {
      console.error(aliasb03645cf6abd4d56a73b13687765a5d8.t('key1001593'), e);
      return null;
    }
  },

  /**
   * 销毁当前 websocket 连接。
   * @param {boolean} clearListeners 是否同时清空所有消息监听器。
   */
  destroyWebSocket(clearListeners = false) {
    this.heartReset();
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.onclose = null;
      if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close();
      }
    }
    this.socket = null;
    this.socketUrl = '';
    if (clearListeners) {
      this.messageListeners.clear();
    }
  }
};
